<template>
  <BaseCard :title="'POVO Class Registration Interface'" :highlightLastWord="false">
    <div class="flex flex-col justify-center gap-4">
      <div class="lg:w-full grid lg:grid-cols-2 lg:gap-20">
        <h5 class="lg:justify-self-end lg:pr-6 font-medium">Project Leader</h5>
        <h5 class="lg:justify-self-start lg:pl-6 font-medium">January 2025 - January 2026</h5>
      </div>

      <p class="text-center items-center px-4 gap-2 justify-center">
        <span class="block">As part of my Human Computer Interaction course, my professor tasked my group of 3 to implement a full-scale redesign
        of a platform of our choice, one primarily that fails to address user needs and HCI-based principles. Given that 
        we had heard thousands of complaints about Notre Dame's course registration softwares (yes, two separate softwares),
        PATH and NOVO, we focused on combining these two into one full-fledged platform that handled both course search and
        registration with speed and ease of use.</span>
        <span class="block">We broke down our project into 4 sprints: user research, brainstorming/prototyping, Figma design and web prototyping, 
        and summative user evaluation.</span> 
        <span class="block">
          In Sprint 1, we conducted 7 user interviews spanning all 4 undergraduate years, graduate students, faculty, and various majors.
          We designed our interview process to capture diverse perspectives, and we found over 50 failures in satisfying user needs.
        </span>
        <span class="block">
          These interviews then led into Sprint 2, where we drew about 150 sketches to iterate through users' needs and our ideas on addressing them.
          Because of our differing interviews, each of us drew entirely different ideas taht emphasized different needs, yet I worked with the my two group members
          to combine our ideas into 6 "How Might We" statements that addressed the primary needs. We then used these statements to implement 6 final design prototypes that we took to users,
          receiving very positive feedback. 
        </span>
        <span class="block">
          Moving forward to Sprint 3, I used my expertise in reactive web design to implement a reactive, single-page prototype
          named <a href="https://povo-interface-online.web.app/" target="_blank" class="photo-link">POVO</a>. Taking our prototype to 3 PhD students and our peers,
          we received immensely positive feedback, from students asking "Will this be released next week in time for registration?" to "Can you please take this to the administration?"
          Even still, our work wasn't finished as we needed to improve from critical feedback and evaluate our prototype. 
        </span>
        <span class="block">
          In Sprint 4, each of us conducted
          a heurstic evaluation first to gather potential improvements that I later implemented in the POVO prototype. Then, we conducted 11 summative evaluations, with users taking on 
          3 tasks each that reflected the general flow of course registration throughout PATH/NOVO and POVO. In these evaluations, we kept track of users' comments as well as their completion time, frustration, 
          and NASA TLX Evaluaton Surveys. In the quantitative data, we found the following very positive results:
        </span>
      </p>
      <ul class="text-center items-center justify-center">
        <li>A 90% decrease in task completion time between PATH/NOVO and POVO</li>
        <li>3.73/20 average mental demand from TLX survey (lower than PATH/NOVO)</li>
        <li>4.0/20 average temporal demand (much lower than PATH/NOVO)</li>
        <li>4.06/20 average effort (slightly lower than PATH/NOVO)</li>
        <li>3.4/20 average frustration (staggeringly lower than PATH/NOVO)</li>
      </ul> 
      <p>
        For more details, check out our nearly 70-page 
        <a href="https://docs.google.com/document/d/1FWvtfMxiwoWXePhVTn7zi39D3eHltyPQMtqWOpfCSvA/edit?usp=sharing" target="_blank" class="link-in-text">Design Document</a> detailing all 4 sprints
        or the <a href="https://povo-interface-online.web.app/" target="_blank" class="link-in-text">POVO prototype</a> itself!
      </p>
      <span class="flex flex-col place-items-center overflow-hidden">
        <img class="w-[50%] h-auto rounded-lg cursor-pointer" :src="PovoPrototype" @click="openImageMenu(PovoPrototype, 'POVO Interface', $event.currentTarget)">
      </span>
    </div>
  </BaseCard>
</template>

<script lang="ts" setup>
import BaseCard from '@/features/global/BaseCard.vue';
import PovoPrototype from '@/assets/Projects/PovoPhotos/PovoPrototype.jpg';
import { useImageViewer } from '@/features/composables/useImageViewer';

const { openImageMenu } = useImageViewer();
</script>